import { apiFetch } from './client.ts';

export interface LeaderboardUser {
  rank: number;
  user: {
    id: string;
    username: string;
    country: string;
    avatarFilename?: string | null;
  };
  score: number;
}

export interface LeaderboardPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface LeaderboardResponse {
  users: LeaderboardUser[];
  currentUser: LeaderboardUser | null;
  pagination: LeaderboardPagination;
}

type LeaderboardParams = { limit?: number; page?: number };

function leaderboardQuery(params?: LeaderboardParams): string {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.page) query.set('page', String(params.page));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export function getEloLeaderboard(
  params?: LeaderboardParams,
  token?: string,
): Promise<LeaderboardResponse> {
  return apiFetch(`/api/leaderboard/elo${leaderboardQuery(params)}`, { token });
}

export function getStreakLeaderboard(
  params?: LeaderboardParams,
  token?: string,
): Promise<LeaderboardResponse> {
  return apiFetch(`/api/leaderboard/streak${leaderboardQuery(params)}`, { token });
}

export function getCommunityLeaderboard(
  params?: LeaderboardParams,
  token?: string,
): Promise<LeaderboardResponse> {
  return apiFetch(`/api/leaderboard/community${leaderboardQuery(params)}`, { token });
}

interface SpeedrunEntry {
  userId: string;
  username: string;
  avatarFilename: string | null;
  bestTimeMs: number;
  updatedAt: string;
  rank: number;
}

interface SpeedrunLeaderboardResult {
  entries: SpeedrunEntry[];
  myBest: SpeedrunEntry | null;
  myRank: number | null;
  pagination: LeaderboardPagination;
}

export async function getSpeedrunLeaderboard(
  params?: LeaderboardParams,
  token?: string,
): Promise<LeaderboardResponse> {
  const data = await apiFetch<SpeedrunLeaderboardResult>(
    `/api/speedrun/leaderboard${leaderboardQuery(params)}`,
    { token },
  );
  const users: LeaderboardUser[] = data.entries.map((entry) => ({
    rank: entry.rank,
    user: { id: entry.userId, username: entry.username, country: '', avatarFilename: entry.avatarFilename ?? null },
    score: entry.bestTimeMs,
  }));
  let currentUser: LeaderboardUser | null = null;
  if (data.myBest && data.myRank != null) {
    currentUser = {
      rank: data.myRank,
      user: { id: data.myBest.userId, username: data.myBest.username, country: '', avatarFilename: data.myBest.avatarFilename ?? null },
      score: data.myBest.bestTimeMs,
    };
  }
  return { users, currentUser, pagination: data.pagination };
}
